/**
 * Avi Fix
 */
import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * Cache of fetched section HTML keyed by page number.
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {string | null} */
  #imageRatioSetting = null;

  /** @type {boolean} Guard to prevent duplicate next-page renders */
  #isRenderingNext = false;

  /** @type {boolean} Guard to prevent duplicate previous-page renders */
  #isRenderingPrevious = false;

  /**
   * Track which page numbers are in-flight to avoid duplicate network requests.
   * @type {Set<number>}
   */
  #fetchingPages = new Set();

  /**
   * The highest page number currently loaded into the DOM.
   * Tracked independently of window.location for accuracy during fast scrolling.
   * @type {number}
   */
  #currentMaxPage = 1;

  /**
   * The lowest page number currently loaded into the DOM.
   * @type {number}
   */
  #currentMinPage = 1;

  /**
   * How many pages ahead to pre-fetch simultaneously.
   * 2 means pages N+1 and N+2 are always cached before the user needs them.
   * @type {number}
   */
  static PREFETCH_AHEAD = 2;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();
    this.#initCurrentPage();

    // Kick off PREFETCH_AHEAD background fetches in both directions immediately
    this.#prefetchAhead('next', PaginatedList.PREFETCH_AHEAD);
    this.#prefetchAhead('previous', PaginatedList.PREFETCH_AHEAD);

    this.#observeViewMore();
  }

  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;
    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  /**
   * Initialise #currentMinPage / #currentMaxPage from card data-page attributes.
   * This is more reliable than parsing window.location during fast scrolling.
   */
  #initCurrentPage() {
    const { cards } = this.refs;
    if (!Array.isArray(cards) || !cards.length) return;

    const first = Number(cards[0]?.dataset?.page ?? 1);
    const last  = Number(cards[cards.length - 1]?.dataset?.page ?? first);

    this.#currentMinPage = first;
    this.#currentMaxPage = last;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

  }

  // ─── Observer ──────────────────────────────────────────────────────────────

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious || !viewMoreNext) return;

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        if (viewTransition.current) await viewTransition.current;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (entry.target === viewMorePrevious) {
              this.#renderPreviousPage();
            } else {
              this.#renderNextPage();
            }
          }
        }
      },
      {
        // 600px look-ahead: observer fires well before the sentinel scrolls into
        // view, giving the Section Rendering API time to respond.
        // Combined with PREFETCH_AHEAD=2, fast scrollers never see a blank gap.
        rootMargin: '0px 0px 600px 0px',
      }
    );

    this.infinityScrollObserver.observe(viewMorePrevious);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  // ─── Pre-fetching ──────────────────────────────────────────────────────────

  /**
   * Fetch a single page by number and cache the HTML.
   * Silently skips if: out of range, already cached, or already in-flight.
   * @param {number} pageNum
   */
  async #fetchPageByNumber(pageNum) {
    if (pageNum < 1 || pageNum > this.#getLastPage()) return;
    if (this.pages.has(pageNum)) return;
    if (this.#fetchingPages.has(pageNum)) return;

    this.#fetchingPages.add(pageNum);

    const url = new URL(window.location.href);
    url.searchParams.set('page', pageNum.toString());
    url.hash = '';

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
      this.pages.set(pageNum, html);

      // Unblock any render that was awaiting this specific page
      if (pageNum === this.#currentMaxPage + 1) {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      }
      if (pageNum === this.#currentMinPage - 1) {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    } finally {
      this.#fetchingPages.delete(pageNum);
    }
  }

  /**
   * Fire-and-forget: kick off `count` background pre-fetches in one direction.
   * @param {'next'|'previous'} direction
   * @param {number} count
   */
  #prefetchAhead(direction, count) {
    for (let i = 1; i <= count; i++) {
      const pageNum = direction === 'next'
        ? this.#currentMaxPage + i
        : this.#currentMinPage - i;
      this.#fetchPageByNumber(pageNum); // intentionally no await
    }
  }

  // ─── Rendering ─────────────────────────────────────────────────────────────

  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return;
    if (this.#isRenderingNext) return;

    const targetPage = this.#currentMaxPage + 1;
    if (targetPage > this.#getLastPage()) return;

    this.#isRenderingNext = true;

    // Show skeleton placeholder immediately — fills the height gap visually
    const skeleton = this.#showSkeleton('next');

    // In the happy path (pre-fetch worked), this is instant (already cached)
    let items = this.#getGridForPage(targetPage);

    if (!items) {
      // Cold path: pre-fetch wasn't ready — start it now and wait
      this.#fetchPageByNumber(targetPage);
      await new Promise((res) => { this.#resolveNextPagePromise = res; });
      items = this.#getGridForPage(targetPage);
    }

    skeleton?.remove();

    if (!items) {
      this.#isRenderingNext = false;
      return;
    }

    grid.append(...items);
    this.#currentMaxPage = targetPage;

    this.#processNewElements();
    this.#isRenderingNext = false;

    // Immediately pre-fetch next pages — no requestIdleCallback delay
    this.#prefetchAhead('next', PaginatedList.PREFETCH_AHEAD);
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid) return;
    if (this.#isRenderingPrevious) return;

    const targetPage = this.#currentMinPage - 1;
    if (targetPage < 1) return;

    this.#isRenderingPrevious = true;

    const skeleton = this.#showSkeleton('previous');

    let items = this.#getGridForPage(targetPage);

    if (!items) {
      this.#fetchPageByNumber(targetPage);
      await new Promise((res) => { this.#resolvePreviousPagePromise = res; });
      items = this.#getGridForPage(targetPage);
    }

    skeleton?.remove();

    if (!items) {
      this.#isRenderingPrevious = false;
      return;
    }

    // Capture scroll position before DOM mutation to prevent viewport jump
    const scrollTop = window.scrollY;
    const firstEl   = grid.firstElementChild;
    const oldTop    = firstEl ? firstEl.getBoundingClientRect().top + window.scrollY : 0;

    grid.prepend(...items);
    this.#currentMinPage = targetPage;

    this.#processNewElements();
    if (firstEl) {
      const newTop     = firstEl.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newTop - oldTop;
      window.scrollTo({ top: scrollTop + heightDiff, behavior: 'instant' });
    }

    this.#isRenderingPrevious = false;

    this.#prefetchAhead('previous', PaginatedList.PREFETCH_AHEAD);
  }

  // ─── Skeleton cards ────────────────────────────────────────────────────────

  /**
   * Inject skeleton placeholder cards adjacent to the grid.
   * This gives the layout reserved height while awaiting the real page,
   * preventing the page from looking frozen or causing layout shift.
   *
   * @param {'next'|'previous'} direction
   * @returns {HTMLElement|null}
   */
  #showSkeleton(direction) {
    const { grid } = this.refs;
    if (!grid) return null;

    // Mirror the count of real cards currently visible (capped at page size)
    const liveCards    = grid.querySelectorAll('.product-grid__item:not(.ssr-skeleton-item)');
    const skeletonCount = Math.min(Math.max(liveCards.length, 4), 18);

    // Inherit grid classes so skeleton uses the same column layout
    const wrapper = document.createElement('ul');
    wrapper.className = grid.className;
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.removeAttribute('ref'); // don't let Component pick it up as a ref

    for (let i = 0; i < skeletonCount; i++) {
      const li = document.createElement('li');
      li.className = 'product-grid__item ssr-skeleton-item';
      li.innerHTML = `
        <div class="ssr-skeleton-card">
          <div class="ssr-skeleton-img ssr-shimmer"></div>
          <div class="ssr-skeleton-title ssr-shimmer"></div>
          <div class="ssr-skeleton-price ssr-shimmer"></div>
        </div>`;
      wrapper.appendChild(li);
    }

    if (direction === 'next') {
      grid.parentNode.insertBefore(wrapper, grid.nextSibling);
    } else {
      grid.parentNode.insertBefore(wrapper, grid);
    }

    return wrapper;
  }

  // ─── Aspect ratio handling ─────────────────────────────────────────────────

  #processNewElements() {
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt'
        ? this.#fixAdaptiveAspectRatios()
        : this.#applyFixedAspectRatio();
    });
  }

  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);
    gallery.querySelectorAll('.product-media-container').forEach((c) => {
      if (c instanceof HTMLElement) c.style.aspectRatio = aspectRatio;
    });
    this.#markAsProcessed(gallery);
  }

  #fixAdaptiveAspectRatios() {
    const galleries = this.#getUnprocessedGalleries();
    if (!galleries.length) return;

    const ratioCache = new Map();

    galleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && ratioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, ratioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) { this.#applyAspectRatioToGallery(gallery, '1'); return; }

      const apply = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;
        const ratio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);
        if (productId) ratioCache.set(productId, ratio);
        this.#applyAspectRatioToGallery(gallery, ratio);
      };

      img.complete ? apply() : img.addEventListener('load', apply, { once: true });
    });
  }

  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;
    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const galleries = this.#getUnprocessedGalleries();
    if (!galleries.length) return;

    requestAnimationFrame(() => {
      galleries.forEach((gallery) => {
        if (gallery instanceof HTMLElement) this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  static ASPECT_RATIOS = { square: '1', portrait: '0.8', landscape: '1.778' };

  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  // ─── Utilities ─────────────────────────────────────────────────────────────


  #getLastPage() {
    const { grid } = this.refs;
    if (!grid) return 0;
    return Number(grid.getAttribute('last-page'));
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const doc         = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = doc.querySelector('[ref="grid"]');
    if (!gridElement) return;

    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }
}